import { getLogger } from '../../diagnostics/logger';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  CREATE_BUNDLE_ALERT_DEFINITION,
  CREATE_BUNDLE_ALERT_DEFINITION_PENDING,
  DELETE_BUNDLE_ALERT_DEFINITION,
  ENSURE_BUNDLE_ALERT_DEFINITIONS,
  INITIAL_AUTHENTICATION,
  LOAD_BUNDLE_ALERT_DEFINITIONS,
  UPDATE_BUNDLE_ALERT_DEFINITION,
} from '../../actions/actionTypes';
import {
  createBundleAlertDefinitionFailure,
  createBundleAlertDefinitionSuccess,
  deleteBundleAlertDefinitionFailure,
  deleteBundleAlertDefinitionSuccess,
  loadBundleAlertDefinitions,
  loadBundleAlertDefinitionsFailure,
  loadBundleAlertDefinitionsPending,
  loadBundleAlertDefinitionsSuccess,
  updateBundleAlertDefinitionFailure,
  updateBundleAlertDefinitionSuccess,
} from '../../actions/bundleAlertDefinitionActions';
import { Action, PromiseAction } from '../../actions/commonActions';
import { addNotification } from '../../actions/notificationActions';
import * as definitionApi from '../../api/bundleAlertDefinitionApi';
import { SimManagementError } from '../../models/Errors';
import { BundleAlertDefinitionsState } from '../../models/state/bundleAlertDefinitionsState';
import {
  BundleAlertDefinition,
  BundleAlertDefinitionInputCreate,
  BundleAlertDefinitionInputUpdate,
  getAlertTypeText,
} from '../../models/transfer/input/BundleAlertDefinition';
import { selectBundleAlertDefinitions } from '../../selectors/bundleAlertDefinitions';

const log = getLogger('simmanagement.sagas.login');

/**
 * Ensure that the bundle alert definitions have been loaded.
 * @param action The action that invoked the saga.
 */
export function* ensureBundleAlertDefinitionsSaga(action: Action<number | string>) {
  const { payload } = action;
  if (typeof payload === 'undefined') {
    log.error('ensureBundleAlertDefinitionsSaga payload is undefined');
    return;
  }

  const state: BundleAlertDefinitionsState = yield select(selectBundleAlertDefinitions);
  if (payload !== state.filter || (state.expiresOn && state.expiresOn < new Date())) {
    yield put(loadBundleAlertDefinitions(payload));
  }
}

/**
 * Load the bundle alert definitions.
 * @param action The action that invoked the saga.
 */
export function* loadBundleAlertDefinitionsSaga(action: Action<number | string>) {
  const { payload } = action;
  if (typeof payload === 'undefined') {
    log.error('loadBundleAlertDefinitionsSaga payload is undefined');
    return;
  }

  yield put(loadBundleAlertDefinitionsPending(payload));
  try {
    const response: BundleAlertDefinition[] = yield call(definitionApi.get, payload);
    yield put(loadBundleAlertDefinitionsSuccess(response, payload));
  } catch (error: any) {
    yield put(loadBundleAlertDefinitionsFailure(error, payload));
  }
}

/*
 * A saga which creates a bundle alert definition on the server.
 * @param action
 */
export function* createBundleAlertDefinitionSaga(
  action: PromiseAction<BundleAlertDefinitionInputCreate, BundleAlertDefinition>
) {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    action.reject && action.reject('No payload.');
    return;
  }

  const definition = action.payload;
  try {
    yield put({ type: CREATE_BUNDLE_ALERT_DEFINITION_PENDING });
    const bundleAlertDefinition: BundleAlertDefinition = yield call(definitionApi.create, definition);
    yield put(createBundleAlertDefinitionSuccess(bundleAlertDefinition));
    yield put(
      addNotification({
        category: 'task',
        title: 'SMS Alerts Configured',
        detail: `${definition.alertType} SMS alerts for account ${definition.accountId} were configured successfully.`,
        type: 'success',
        showInCount: false,
      })
    );
    action.resolve && action.resolve(bundleAlertDefinition);
  } catch (error: any) {
    const err = error as SimManagementError;
    yield put(createBundleAlertDefinitionFailure(err));
    if (!err.validationErrors) {
      yield put(
        addNotification({
          category: 'task',
          title: 'Error',
          detail: `An error occurred while attempting to configure SMS alerts for account ${definition.accountId}.`,
          type: 'error',
          showInCount: false,
        })
      );
    }

    action.reject && action.reject(err);
  }
}

/**
 * Saga which contains logic for updating an existing bundle alert definition.
 * @param action The action which invoked the saga.
 */
export function* updateBundleAlertDefinitionSaga(
  action: PromiseAction<BundleAlertDefinitionInputUpdate, BundleAlertDefinition>
) {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    action.reject && action.reject('No payload.');
    return;
  }

  const definition = action.payload;
  try {
    const updatedDefinition = (yield call(definitionApi.update, definition)) as BundleAlertDefinition;
    yield put(updateBundleAlertDefinitionSuccess(updatedDefinition));
    yield put(
      addNotification({
        category: 'task',
        title: `${updatedDefinition.alertType} Alert Configuration Updated`,
        detail: `The ${updatedDefinition.alertType} Alert Configuration was updated successfully.`,
        type: 'success',
        showInCount: false,
      })
    );

    action.resolve && action.resolve(updatedDefinition);
  } catch (error: any) {
    const err = error as SimManagementError;
    yield put(updateBundleAlertDefinitionFailure(err));
    if (!err.validationErrors) {
      yield put(
        addNotification({
          category: 'task',
          title: 'Error',
          detail: 'An error occurred while attempting to update the alert configuration.',
          type: 'error',
          showInCount: false,
        })
      );
    }

    action.reject && action.reject(err);
  }
}

/**
 * A saga which deletes a bundle alert definition from the server.
 * @param action
 */
export function* deleteBundleAlertDefinitionSaga(action: PromiseAction<BundleAlertDefinition, BundleAlertDefinition>) {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    action.reject && action.reject('No payload.');
    return;
  }

  const definition = action.payload;
  const { alertType, accountId } = definition;
  try {
    yield call(definitionApi.remove, definition.id);
    yield put(deleteBundleAlertDefinitionSuccess(definition));
    const alertTypeText = getAlertTypeText(alertType);
    yield put(
      addNotification({
        category: 'task',
        title: 'SMS Alerts Configured',
        detail: `${alertTypeText} SMS alerts for account ${accountId} were deconfigured successfully.`,
        type: 'success',
        showInCount: false,
      })
    );

    action.resolve && action.resolve(definition);
  } catch (error: any) {
    const err = error as SimManagementError;
    put(deleteBundleAlertDefinitionFailure(err));
    yield put(
      addNotification({
        category: 'task',
        title: 'Error',
        detail: `An error occurred while attempting to deconfigure SMS alerts for account ${accountId}.`,
        type: 'error',
        showInCount: false,
      })
    );

    action.reject && action.reject(err);
  }
}

/*
 * Initialize all sagas related to bundle alert definitions
 */
export function* initBundleAlertDefinitionSagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(CREATE_BUNDLE_ALERT_DEFINITION, createBundleAlertDefinitionSaga);
  yield takeEvery(DELETE_BUNDLE_ALERT_DEFINITION, deleteBundleAlertDefinitionSaga);
  yield takeEvery(ENSURE_BUNDLE_ALERT_DEFINITIONS, ensureBundleAlertDefinitionsSaga);
  yield takeEvery(LOAD_BUNDLE_ALERT_DEFINITIONS, loadBundleAlertDefinitionsSaga);
  yield takeEvery(UPDATE_BUNDLE_ALERT_DEFINITION, updateBundleAlertDefinitionSaga);
}
